'use client';

import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { AlertCircle, AlertTriangle, FileWarning, TrendingUp } from 'lucide-react';
import { api } from '@/shared/services/api';
import { formatNumber } from '@/shared/utils/number-format';

interface VatThresholdStatus {
  applicable: boolean;
  vatRegime: string;
  threshold: number;
  currentTurnover: number;
  percentage: number;
  status: 'safe' | 'warning' | 'critical' | 'exceeded';
  legacyVatInvoicesCount: number;
  /** Facturi în valută fără curs — excluse din `currentTurnover`. */
  missingRateCount?: number;
  missingRateCurrencies?: string[];
}

/**
 * Banner dashboard pentru tenants neplătitori TVA. Două funcții independente:
 *   1. Alertă plafon 395.000 RON (art. 310 Cod Fiscal) — progresivă în 4 trepte
 *   2. Warning facturi istorice cu TVA (eroare fiscală pre-Faza 1)
 *
 * Pentru tenants `payer` (deja plătitori), banner-ul e ascuns complet.
 * Pentru tenants `non_payer` cu cifră de afaceri <70% și fără facturi
 * istorice problematice, banner-ul e tot ascuns (zero zgomot vizual).
 */
export function VatThresholdBanner() {
  const { data: status } = useQuery<VatThresholdStatus>({
    queryKey: ['vat-threshold-status'],
    queryFn: () => api.get<VatThresholdStatus>('/tenants/vat-threshold-status'),
    staleTime: 5 * 60 * 1000, // 5 min — turnover nu se schimbă rapid
  });

  if (!status) return null;

  // Semnalul „facturi fără curs" NU e condiționat de `applicable`: plafonul
  // micro (100.000 EUR) e cu ~30% peste plafonul de scutire TVA, deci orice
  // firmă apropiată de el e deja plătitoare de TVA — exact tenanții pentru
  // care restul bannerului se ascunde. Gating-ul l-ar fi făcut invizibil
  // tocmai cui îi folosea.
  const missingRateCount = status.missingRateCount ?? 0;
  const showIncompleteAlert = missingRateCount > 0;
  if (!status.applicable) {
    return showIncompleteAlert ? (
      <IncompleteRevenueAlert count={missingRateCount} currencies={status.missingRateCurrencies ?? []} />
    ) : null;
  }

  const showThresholdAlert = status.status !== 'safe';
  const showLegacyAlert = status.legacyVatInvoicesCount > 0;

  if (!showThresholdAlert && !showLegacyAlert && !showIncompleteAlert) return null;

  return (
    <div className="space-y-3">
      {showThresholdAlert && <ThresholdAlert status={status} />}
      {showIncompleteAlert && (
        <IncompleteRevenueAlert
          count={missingRateCount}
          currencies={status.missingRateCurrencies ?? []}
          turnover={status.currentTurnover}
        />
      )}
      {showLegacyAlert && <LegacyVatAlert count={status.legacyVatInvoicesCount} />}
    </div>
  );
}

/**
 * Cifra de afaceri e o limită inferioară: documentele în valută fără curs nu
 * pot fi convertite în lei, deci lipsesc din total. Ton informativ, nu de
 * alarmă — problema e a datelor, iar remedierea e la un click distanță.
 */
function IncompleteRevenueAlert({
  count, currencies, turnover,
}: { count: number; currencies: string[]; turnover?: number }) {
  const plural = count !== 1;
  return (
    <div className="rounded-lg border-2 border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30 p-4">
      <div className="flex items-start gap-3">
        <FileWarning className="h-5 w-5 text-sky-700 dark:text-sky-300 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-sky-900 dark:text-sky-100 mb-1">
            Cifra de afaceri este incompletă · {count} {plural ? 'documente' : 'document'} fără curs valutar
          </h3>
          <p className="text-[0.8125rem] leading-snug text-sky-800 dark:text-sky-200 mb-2">
            {count} {plural ? 'documente emise' : 'document emis'} în valută
            {currencies.length > 0 && <> ({currencies.join(', ')})</>} nu {plural ? 'au' : 'are'} curs
            valutar completat, deci nu intră în cifra de afaceri calculată
            {typeof turnover === 'number' && (
              <> (<strong className="tabular-nums">{formatNumber(turnover, 0)} RON</strong>)</>
            )}
            . Completează cursul pe fiecare document, ca plafonul să fie urmărit corect.
          </p>
          <Link
            href="/facturi?period=all"
            className="inline-flex items-center gap-1 text-[0.8125rem] font-semibold text-sky-700 dark:text-sky-300 hover:text-sky-900 dark:hover:text-sky-100 underline"
          >
            Vezi facturile →
          </Link>
        </div>
      </div>
    </div>
  );
}

function ThresholdAlert({ status }: { status: VatThresholdStatus }) {
  const fmt = (n: number) => formatNumber(n, 0);
  const STYLES = {
    warning: {
      bg: 'bg-amber-50 dark:bg-amber-950/30',
      border: 'border-amber-300 dark:border-amber-800',
      title: 'text-amber-900 dark:text-amber-100',
      text: 'text-amber-800 dark:text-amber-200',
      bar: 'bg-amber-400 dark:bg-amber-500',
      barBg: 'bg-amber-100 dark:bg-amber-900/40',
      icon: TrendingUp,
      label: 'Atenție',
    },
    critical: {
      bg: 'bg-orange-50 dark:bg-orange-950/30',
      border: 'border-orange-400 dark:border-orange-700',
      title: 'text-orange-900 dark:text-orange-100',
      text: 'text-orange-800 dark:text-orange-200',
      bar: 'bg-orange-500 dark:bg-orange-500',
      barBg: 'bg-orange-100 dark:bg-orange-900/40',
      icon: AlertTriangle,
      label: 'Critic',
    },
    exceeded: {
      bg: 'bg-red-50 dark:bg-red-950/30',
      border: 'border-red-500 dark:border-red-700',
      title: 'text-red-900 dark:text-red-100',
      text: 'text-red-800 dark:text-red-200',
      bar: 'bg-red-600 dark:bg-red-500',
      barBg: 'bg-red-100 dark:bg-red-900/40',
      icon: AlertCircle,
      label: 'Plafon depășit',
    },
  } as const;
  const s = STYLES[status.status as keyof typeof STYLES] || STYLES.warning;
  const Icon = s.icon;

  const message = (() => {
    if (status.status === 'exceeded') {
      return (
        <>
          Cifra de afaceri pe anul curent a <strong>depășit plafonul</strong> de scutire TVA.
          Conform <em>art. 310 alin. (6)</em>, ești obligat să te înregistrezi ca plătitor TVA{' '}
          <strong>cel târziu la data depășirii</strong>.{' '}
          <a
            href="https://www.anaf.ro/anaf/internet/ANAF/asistenta_contribuabili/contribuabili_persoane_fizice/inregistrare_persoane_fizice/inregistrare_TVA/inregistrare_in_scopuri_TVA"
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-medium"
          >
            Vezi procedura ANAF →
          </a>
        </>
      );
    }
    if (status.status === 'critical') {
      return (
        <>
          Ai atins <strong>{status.percentage}%</strong> din plafonul TVA. Mai poți emite facturi de aproximativ{' '}
          <strong>{fmt(status.threshold - status.currentTurnover)} RON</strong> înainte să fii obligat să devii plătitor TVA.
          Pregătește documentele pentru înregistrarea ANAF.
        </>
      );
    }
    return (
      <>
        Ai atins <strong>{status.percentage}%</strong> din plafonul TVA pentru anul curent.
        Continuând în ritmul actual, poți depăși plafonul în lunile următoare.
      </>
    );
  })();

  return (
    <div className={`rounded-lg border-2 ${s.border} ${s.bg} p-4`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 ${s.title} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 mb-1.5">
            <h3 className={`text-sm font-bold ${s.title}`}>
              {s.label} · Plafon TVA art. 310
            </h3>
            <span className={`text-xs font-semibold tabular-nums ${s.text}`}>
              {fmt(status.currentTurnover)} / {fmt(status.threshold)} RON
            </span>
          </div>
          {/* Progress bar */}
          <div className={`h-2 w-full rounded-full overflow-hidden ${s.barBg} mb-2`}>
            <div
              className={`h-full ${s.bar} transition-all duration-500`}
              style={{ width: `${Math.min(100, status.percentage)}%` }}
              role="progressbar"
              aria-valuenow={status.percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <p className={`text-[0.8125rem] leading-snug ${s.text}`}>{message}</p>
        </div>
      </div>
    </div>
  );
}

function LegacyVatAlert({ count }: { count: number }) {
  return (
    <div className="rounded-lg border-2 border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 p-4">
      <div className="flex items-start gap-3">
        <FileWarning className="h-5 w-5 text-rose-700 dark:text-rose-300 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-rose-900 dark:text-rose-100 mb-1">
            Atenție fiscal · {count} factur{count === 1 ? 'ă' : 'i'} cu TVA emis{count === 1 ? 'ă' : 'e'} de firmă neplătitoare
          </h3>
          <p className="text-[0.8125rem] leading-snug text-rose-800 dark:text-rose-200 mb-2">
            Ai{' '}
            <strong>
              {count} factur{count === 1 ? 'ă' : 'i'}
            </strong>{' '}
            emis{count === 1 ? 'ă' : 'e'} cu TVA, dar firma ta este neplătitoare TVA (regim art. 310). Aceste facturi
            nu sunt valide fiscal — recomandăm <strong>storno + reemitere fără TVA</strong>.
          </p>
          <Link
            href="/facturi?vatTotalGt=0"
            className="inline-flex items-center gap-1 text-[0.8125rem] font-semibold text-rose-700 dark:text-rose-300 hover:text-rose-900 dark:hover:text-rose-100 underline"
          >
            Vezi facturile problematice →
          </Link>
        </div>
      </div>
    </div>
  );
}
